/**
 * THE NEUTRAL CLUVIK TILE — one geometry, drawn in one place.
 *
 * ─── WHY THIS IS ITS OWN FILE ────────────────────────────────────────────
 *
 * Two things in this application need the same object: a rounded tile on the
 * Cluvik ramp carrying one or two letters, standing in for artwork we either
 * do not have (a provider's brand mark) or would not be right to invent (a
 * customer's own logo). They were about to become two drawings of the same
 * thing in two files, which is how one of them acquires a gradient, or a
 * lighter stop, or a second letter size, and nobody notices because each file
 * looks internally consistent.
 *
 * The contrast rule is the reason it matters more than tidiness. The letters
 * are WHITE on the ramp's DARKEST stop — `RAMP[key][2]` — and that index is
 * not a preference: at index 1 the cyan stop `#00C8D8` measures 1.76:1 against
 * white and the letters stop being text. One copy of that decision can be
 * asserted once and stay true for every caller. Two copies is a rule in one
 * file and a coincidence in the other.
 *
 * ─── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────
 *
 * NO GRADIENT BEHIND THE LETTERS. A gradient makes "the contrast" a range
 * rather than a number, and the light end of the range is where the text stops
 * being legible. The tile gets its depth from ONE lit edge along the top and a
 * stroke at the ramp's `mid`, both of which sit clear of the letters.
 *
 * NO THEME BRANCH. The tile is opaque and carries its own contrast, so it is
 * the same artwork in light and in dark — which is the point of drawing depth
 * with geometry instead of with a filter. `IdentityMark`'s bed reads a CSS
 * variable because it is a translucent stage; this tile is not.
 *
 * ─── THIS IS NOT DIMENSIONAL IDENTITY ARTWORK ────────────────────────────
 *
 * `IdentityMark` is the Direction A dimensional treatment and it names a PLACE
 * — an area of the product you can navigate to. This tile names a THING that
 * has no artwork: it is quieter on purpose, and the two must not converge. A
 * tenant row is a link to a place, but the tenant itself is not one of the
 * eight areas, and giving it the area treatment would put nine identities in
 * an eight-identity system.
 */
import React from 'react';
import { RAMP, RAMP_KEYS } from '../tokens/identityTokens';

/** The artwork square. Shared so both callers use one viewBox. */
export const TILE_VIEWBOX = 64;

/**
 * A stable ramp position from an arbitrary key.
 *
 * HASHED ON AN IDENTIFIER, NEVER ON A DISPLAY NAME, and never on a single
 * letter. The platform's own fallback hashes the provider's FIRST LETTER, so
 * two unrelated subjects sharing an initial get identical tiles and a subject's
 * colour changes the day somebody edits its caption. Colour that carries no
 * meaning still reads as though it does; colour that changes when a caption
 * changes is worse, because a reader who learned the tile has to unlearn it.
 *
 * Callers with a real ordering — `ProviderMark` has a served catalogue — should
 * use the position in that ordering instead, which cannot collide at all. This
 * is for the open sets, where there is no ordering to use.
 */
export const rampFromKey = (key) => {
  const s = String(key || '');
  if (!s) return RAMP_KEYS[0];
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return RAMP_KEYS[h % RAMP_KEYS.length];
};

/**
 * Up to two initials, mechanically.
 *
 * First letter of each of the first two words. It expands nothing and guesses
 * nothing: every richer rule — stripping "Ltd", knowing "3" belongs to
 * "Microsoft 365", recognising an acronym — is an assumption about a name this
 * build has never been told about, and it is wrong in public the first time it
 * meets one it does not know.
 */
export const initialsFrom = (source) => {
  const s = String(source || '').trim();
  if (!s) return '?';
  const words = s.split(/[\s_-]+/).filter(Boolean);
  const letters = words.slice(0, 2).map((w) => w[0]).join('');
  return (letters || s[0]).toUpperCase().slice(0, 2);
};

/**
 * The tile itself, as SVG children — deliberately not a whole `<svg>`, so each
 * caller owns its own root element, its own accessible name and its own
 * `data-` attributes. Those differ (a provider states its artwork position; a
 * tenant states nothing of the kind) and folding them in here would give one
 * caller the other's vocabulary.
 *
 * @param {string} rampKey  A key of `RAMP`. Anything else falls back to blue.
 * @param {string} initials One or two characters. Sized so two still fit.
 */
export function NeutralTileArt({ rampKey, initials }) {
  const [, mid, hi] = RAMP[rampKey] || RAMP.blue;
  return (
    <>
      {/* The body, at the ramp's DARKEST stop — the only stop that carries
          white letters at 4.5:1, asserted in the tests beside this file. */}
      <rect x="0" y="0" width="64" height="64" rx="14" fill={hi} />
      {/* A lit edge drawn INSIDE the tile. `hi` on a dark card measures 1.57:1
          and reads as a smudge; the stroke at `mid` lifts that edge to 2.76:1
          without putting a lighter value anywhere under the text. Inset by half
          the stroke width so the viewBox does not clip it. */}
      <rect
        x="0.75" y="0.75" width="62.5" height="62.5" rx="13.25"
        fill="none" stroke={mid} strokeWidth="1.5" opacity="0.9"
      />
      {/* One lit edge, along the TOP only. Depth without putting a lighter
          value underneath the letters, which is where a gradient would take the
          contrast below the threshold the tests assert. */}
      <path
        d="M14 1 H50 A13 13 0 0 1 63 14 V16 H1 V14 A13 13 0 0 1 14 1 Z"
        fill={mid}
        opacity="0.55"
      />
      <text
        x="32"
        y="33"
        textAnchor="middle"
        dominantBaseline="central"
        fill="#FFFFFF"
        fontSize={String(initials).length > 1 ? 25 : 32}
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif"
        /* Not announced separately — the caller's <title> and the visible label
           beside the mark already say the subject's name. */
        aria-hidden="true"
      >
        {initials}
      </text>
    </>
  );
}

export default NeutralTileArt;
